const BASE = import.meta.env.VITE_API_BASE_URL ?? '';

export interface Agent {
  id: string;
  name: string;
  description: string;
  avatar: string;
  type: string;
  allowed_roles: string[];
  allow_file_upload?: boolean;
}

export interface SessionHistoryItem {
  id: string;
  agent_id: string;
  agent_name: string;
  agent_avatar: string;
  last_message: string;
  timestamp: string;
  title?: string;
}

export interface ChatMessage {
  id?: string;
  text: string;
  sender: string;
  timestamp: string;
}

export interface KnowledgeBase {
  id: string;
  name: string;
  description?: string;
  documents: string[];
}

// Generic named/reusable content: email templates, tone preference, etc.
// owner_id === null means it's a shared/institutional asset visible to everyone.
// Always scoped to a single agent via agent_id — memory/tono/plantillas de un agente
// nunca se mezclan con las de otro (ver desarrolla-agent/HANDOFF.md).
export interface ContentAsset {
  id: string;
  owner_id: string | null;
  kind: string;
  name: string | null;
  content: string;
  agent_id: string | null;
  agent_type?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

interface ChatResponse {
  response: string;
  sessionId: string;
}

export interface UserHeaders {
  userId: string;
  upn: string;
  name: string;
  roles: string[];
}

function authHeaders(user: UserHeaders, token: string): Record<string, string> {
  return {
    'Authorization': `Bearer ${token}`,
    'X-User-Id': user.userId,
    'X-User-UPN': user.upn,
    'X-User-Name': user.name,
    'X-User-Roles': user.roles.join(','),
  };
}

export async function fetchAgents(token: string, user: UserHeaders, type?: string): Promise<Agent[]> {
  const query = type ? `?type=${encodeURIComponent(type)}` : '';
  const res = await fetch(`${BASE}/api/v1/agents${query}`, {
    headers: authHeaders(user, token),
  });
  if (!res.ok) throw new Error('No se pudieron cargar los agentes');
  return res.json();
}

export async function fetchSessions(token: string, user: UserHeaders, type: string = 'chat', agentId?: string): Promise<SessionHistoryItem[]> {
  const query = new URLSearchParams({ type });
  if (agentId) query.set('agent_id', agentId);
  const res = await fetch(`${BASE}/api/v1/sessions?${query.toString()}`, {
    headers: authHeaders(user, token),
  });
  if (!res.ok) throw new Error('No se pudieron cargar las sesiones');
  // El backend pagina: {"items": [...], "has_more": bool}, no un array plano.
  const data: { items: SessionHistoryItem[]; has_more: boolean } = await res.json();
  return data.items;
}

export async function fetchHistory(token: string, user: UserHeaders, sessionId: string): Promise<ChatMessage[]> {
  const res = await fetch(`${BASE}/api/v1/history/${sessionId}`, {
    headers: authHeaders(user, token),
  });
  if (!res.ok) throw new Error('No se pudo cargar el historial');
  return res.json();
}

export async function fetchKnowledgeBases(token: string, user: UserHeaders): Promise<KnowledgeBase[]> {
  const res = await fetch(`${BASE}/api/v1/kbs`, {
    headers: authHeaders(user, token),
  });
  if (!res.ok) throw new Error('No se pudieron cargar las bases de conocimiento');
  return res.json();
}

const TEMPLATE_KIND = 'email_template';
const TONE_KIND = 'tone_preference';

export async function fetchTemplates(token: string, user: UserHeaders, agentId: string): Promise<ContentAsset[]> {
  const res = await fetch(`${BASE}/api/v1/content-assets?kind=${TEMPLATE_KIND}&agent_id=${encodeURIComponent(agentId)}`, {
    headers: authHeaders(user, token),
  });
  if (!res.ok) throw new Error('No se pudieron cargar las plantillas');
  return res.json();
}

export async function saveTemplate(
  token: string,
  user: UserHeaders,
  agentId: string,
  name: string,
  content: string,
): Promise<ContentAsset> {
  const res = await fetch(`${BASE}/api/v1/content-assets`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(user, token) },
    body: JSON.stringify({ kind: TEMPLATE_KIND, agent_id: agentId, name, content }),
  });
  if (!res.ok) throw new Error('No se pudo guardar la plantilla');
  return res.json();
}

export async function deleteTemplate(token: string, user: UserHeaders, id: string): Promise<void> {
  const res = await fetch(`${BASE}/api/v1/content-assets/${id}`, {
    method: 'DELETE',
    headers: authHeaders(user, token),
  });
  if (!res.ok) throw new Error('No se pudo eliminar la plantilla');
}

export async function fetchTonePreference(token: string, user: UserHeaders, agentId: string): Promise<string> {
  const res = await fetch(`${BASE}/api/v1/content-assets?kind=${TONE_KIND}&agent_id=${encodeURIComponent(agentId)}&include_shared=false`, {
    headers: authHeaders(user, token),
  });
  if (!res.ok) throw new Error('No se pudo cargar la preferencia de tono');
  const assets: ContentAsset[] = await res.json();
  return assets[0]?.content ?? '';
}

export async function saveTonePreference(token: string, user: UserHeaders, agentId: string, content: string): Promise<void> {
  const res = await fetch(`${BASE}/api/v1/content-assets`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(user, token) },
    body: JSON.stringify({ kind: TONE_KIND, agent_id: agentId, content }),
  });
  if (!res.ok) throw new Error('No se pudo guardar la preferencia de tono');
}

export async function sendEmailToAgent(
  token: string,
  user: UserHeaders,
  agentId: string,
  emailBody: string,
  emailSubject: string,
  sender: string,
  customInstructions: string,
  sessionId: string,
  onThinking?: (content: string) => void,
): Promise<ChatResponse> {
  const message = buildPrompt(emailSubject, sender, emailBody, customInstructions);

  const res = await fetch(`${BASE}/api/v1/chat/${agentId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(user, token),
    },
    body: JSON.stringify({
      text: message,
      sender: 'user',
      timestamp: new Date().toISOString(),
      session_id: sessionId,
    }),
  });

  if (!res.ok) throw new Error('Error al procesar el correo con el agente');

  if (!res.body) throw new Error('El cuerpo de la respuesta está vacío');
  const responseText = await consumeNdjsonStream(res.body, onThinking);

  return {
    response: responseText,
    sessionId: sessionId,
  };
}

export async function sendChatMessage(
  token: string,
  user: UserHeaders,
  agentId: string,
  text: string,
  sessionId: string,
  onThinking?: (content: string) => void,
  onTextChunk?: (chunk: string) => void,
  signal?: AbortSignal,
): Promise<ChatResponse> {
  const res = await fetch(`${BASE}/api/v1/chat/${agentId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(user, token),
    },
    body: JSON.stringify({
      text,
      sender: 'user',
      timestamp: new Date().toISOString(),
      session_id: sessionId,
    }),
    signal,
  });

  if (!res.ok) throw new Error('Error al comunicarse con el agente');

  if (!res.body) throw new Error('El cuerpo de la respuesta está vacío');
  const responseText = await consumeNdjsonStream(res.body, onThinking, onTextChunk);

  return {
    response: responseText,
    sessionId: sessionId,
  };
}

export interface ExtractedFile {
  filename: string;
  size_bytes: number;
  extracted_text: string;
}

// Sube un archivo adjunto de Chat Libre para extraer su texto en el servidor
// (PDF/DOCX/XLSX). Limite de 20MB — el backend responde 413 si se supera.
// Nota: sin 'Content-Type' manual — el browser arma el boundary de multipart solo.
export async function extractFileText(token: string, user: UserHeaders, file: File): Promise<ExtractedFile> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${BASE}/api/v1/files/extract`, {
    method: 'POST',
    headers: authHeaders(user, token),
    body: formData,
  });
  if (!res.ok) {
    if (res.status === 413) throw new Error(`El archivo '${file.name}' supera el límite de 20 MB.`);
    throw new Error(`No se pudo extraer el contenido de '${file.name}'.`);
  }
  return res.json();
}

// El backend responde en NDJSON (una línea = un objeto JSON), no en texto plano
// ni SSE: {"type": "text"|"thinking"|"tool_call"|"tool_result"|"error", ...}.
// Solo "text" compone la respuesta final; "thinking" es progreso transitorio
// (se reporta vía onThinking, no se acumula) y "error" aborta con ese mensaje.
// onTextChunk es opcional: quien quiera renderizar el texto a medida que llega
// (chat en vivo) lo pasa; sendEmailToAgent no lo necesita, espera el total.
async function consumeNdjsonStream(
  body: ReadableStream<Uint8Array>,
  onThinking?: (content: string) => void,
  onTextChunk?: (chunk: string) => void,
): Promise<string> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let responseText = '';

  const handleLine = (line: string) => {
    if (!line.trim()) return;
    let event: { type?: string; content?: string };
    try {
      event = JSON.parse(line);
    } catch {
      responseText += line;
      return;
    }
    if (event.type === 'text') {
      const chunk = event.content ?? '';
      responseText += chunk;
      onTextChunk?.(chunk);
    } else if (event.type === 'thinking') {
      onThinking?.(event.content ?? '');
    } else if (event.type === 'error') {
      throw new Error(event.content || 'Error al procesar la respuesta del agente');
    }
  };

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';
    for (const line of lines) handleLine(line);
  }
  if (buffer.trim()) handleLine(buffer);

  return responseText;
}

function buildPrompt(
  subject: string,
  sender: string,
  body: string,
  customInstructions: string,
): string {
  const parts = [
    `Procesa el siguiente correo electrónico y genera una respuesta profesional.`,
    ``,
    `**Asunto:** ${subject}`,
    `**Remitente:** ${sender}`,
    ``,
    `**Contenido del correo:**`,
    body,
  ];

  if (customInstructions.trim()) {
    parts.push(``, `**Instrucciones adicionales:**`, customInstructions.trim());
  }

  return parts.join('\n');
}
